/**
 * Pull-direction ball — an upright spinning football with a dashed **spin-axis** line across it, a
 * **pull-direction** arrow, and a **clock/degree dial** ring around it (like the iPad app). The ball is
 * an animated WebP (HTML <img>, so it animates); the dial, axis and arrow are an SVG overlay drawn
 * BEHIND the ball, so the dial sits in the margin and only the arrow/axis tips creep past the ball edge.
 *
 * The spin axis is physically perpendicular to the pull direction, so the axis line is drawn as a
 * horizontal diameter inside the arrow's rotated frame — guaranteeing it stays exactly perpendicular
 * to the arrow (the separately-averaged `spinAxis` value is used only to decide whether to show it).
 */
import './PullDirectionBall.css';
import ballSpin from './ballSpin.webp';
import ballStill from './ballStill.png';
import degreesDial from './degreesDial.png';

// SVG user units (a 120×120 box; the ball fills the middle, leaving a margin for the dial + tips).
const BOX = 120;
const C = BOX / 2;
const BALL_R = 40; // ball radius within the box (leaves a margin for the surrounding degree dial)
const AXIS_R = 44; // half-length of the axis diameter — ~10% longer than the ball radius so the tips creep out

/**
 * Arrow heading in degrees (clockwise from straight up). The arrow points the way the ball is pulled:
 * `180 - pullDirection`. (e.g. pullDirection -40 → 220°, i.e. lower-left / ~7-8 o'clock.)
 */
export function arrowRotationDegrees(pullDirection: number): number {
  return 180 - pullDirection;
}

/**
 * Rotation (deg) applied to the spinning ball sprite so its roll direction points the same way as the
 * arrow. The sprite rolls top→bottom (south = 180°) by default, so a rotation of `-pullDirection` makes
 * the roll direction land on `180 - pullDirection` — exactly the arrow's heading, keeping them in sync.
 */
export function ballRollRotationDegrees(pullDirection: number): number {
  const r = -pullDirection;
  return r === 0 ? 0 : r; // avoid -0 in the transform string
}

type Props = {
  pullDirection: number | null;
  /** Spin-axis angle (deg). The axis line is drawn only when both this and pullDirection are present. */
  spinAxis?: number | null;
  color?: string;
  /** Draw the dial and a still (faded, not spinning) ball instead of an empty box when there is no pull direction. */
  emptyBall?: boolean;
};

// The ball image, as a % of the square container (the rest is margin for the arrow tip / axis).
const BALL_IMG_PCT = ((BALL_R * 2) / BOX) * 100;

const PullDirectionBall = ({ pullDirection, spinAxis = null, color = '#EC691A', emptyBall = false }: Props): React.ReactElement => {
  if (pullDirection === null || pullDirection === undefined) {
    if (!emptyBall) return <div className='pd-ball-placeholder' />;
    return (
      <div className='pd-ball pd-ball--empty' aria-hidden='true'>
        <div className='pd-dial-img' style={{ WebkitMaskImage: `url(${degreesDial})`, maskImage: `url(${degreesDial})` }} />
        {/* No rotation values: a still frame, so the ball does not spin when there is nothing to show. */}
        <img className='pd-ball-img' src={ballStill} alt='' style={{ width: `${BALL_IMG_PCT}%`, height: `${BALL_IMG_PCT}%` }} />
      </div>
    );
  }

  const showAxis = spinAxis !== null && spinAxis !== undefined;
  const rotate = `rotate(${arrowRotationDegrees(pullDirection)} ${C} ${C})`;

  return (
    <div className='pd-ball' aria-label={`Pull direction: ${Math.round(pullDirection)} degrees`}>
      {/* Degree dial (0 at bottom, 180 at top, ±30 increments) — the same artwork as the iPad app,
          drawn behind everything as a static reference the arrow points to. Rendered as a currentColor
          mask (not a fixed-colour image) so the numbers stay legible on both dark and light backgrounds. */}
      <div
        className='pd-dial-img'
        style={{ WebkitMaskImage: `url(${degreesDial})`, maskImage: `url(${degreesDial})` }}
      />
      {/* Axis + arrow BEHIND the ball: their middles are hidden under the ball, only the tips creep out.
          Both live in the same rotated frame, so the horizontal axis stays perpendicular to the up-arrow. */}
      <svg className='pd-ball-overlay' viewBox={`0 0 ${BOX} ${BOX}`} aria-hidden='true'>
        {showAxis && (
          <g transform={rotate}>
            <line
              className='pd-axis'
              x1={C - AXIS_R}
              y1={C}
              x2={C + AXIS_R}
              y2={C}
              stroke={color}
              strokeWidth={3}
              strokeLinecap='butt'
              strokeDasharray='6 5'
              opacity={0.9}
            />
          </g>
        )}
        <g className='pd-arrow' transform={rotate}>
          <line x1={C} y1={C - BALL_R + 10} x2={C} y2={C - BALL_R - 1} stroke={color} strokeWidth={2.5} strokeLinecap='round' />
          <path d={`M ${C} ${C - BALL_R - 8} L ${C - 4.5} ${C - BALL_R + 4} L ${C + 4.5} ${C - BALL_R + 4} Z`} fill={color} />
        </g>
      </svg>

      <img
        className='pd-ball-img'
        src={ballSpin}
        alt=''
        style={{ width: `${BALL_IMG_PCT}%`, height: `${BALL_IMG_PCT}%`, transform: `translate(-50%, -50%) rotate(${ballRollRotationDegrees(pullDirection)}deg)` }}
      />
    </div>
  );
};

export default PullDirectionBall;
